import { Component, OnDestroy, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, finalize, first, map, shareReplay, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { Messages } from 'src/app/common/enums/messages';
import { getBase64 } from 'src/app/common/utils/getBase64';
import { CreateOrUpdateWorkerEmploymentHistoryRecordRequest } from 'src/app/contracts/requests/create-or-update-worker-employment-history-record-request';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { EmploymentHistoryRecordService } from 'src/app/data/employment-history-record.service';
import { WorkerService } from 'src/app/data/worker.service';
import { DictionaryItem } from 'src/app/models/DictionaryItem';
import { EmploymentHistoryRecordDto } from 'src/app/models/dtos/employment-history-record-dto';
import { EmploymentTypeDto } from 'src/app/models/dtos/employment-type-dto';
import { FileDto } from 'src/app/models/dtos/file-dto';
import { EmploymentType } from 'src/app/models/enums/employment-type-enum';
import { AlertType } from 'src/app/shared/components/alert/alert.component';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

@Component({
  selector: 'app-employment-history-record-modal',
  templateUrl: './employment-history-record-modal.component.html',
  styleUrls: ['./employment-history-record-modal.component.scss'],
  standalone: false
})
export class EmploymentHistoryRecordModalComponent implements OnDestroy {
  data = inject<{
    workerId: number;
    employmentHistoryRecordId?: number;
  }>(MAT_DIALOG_DATA);
  private dictionaryService = inject(DictionaryService);
  private snackbarService = inject(SnackBarService);
  private spinner = inject(NgxSpinnerService);
  private workerService = inject(WorkerService);
  private formBuilder = inject(UntypedFormBuilder);
  private dialogRef = inject<MatDialogRef<EmploymentHistoryRecordModalComponent>>(MatDialogRef);
  private employmentHistoryRecordService = inject(EmploymentHistoryRecordService);

  employmentHistoryRecordForm: UntypedFormGroup;

  readonly maxFileCount: number = 5;

  agreementTerminationMethods$: Observable<DictionaryItem[]> = this.dictionaryService.getAgreementTerminationMethods();
  employers$: Observable<DictionaryItem[]>;
  employmentTypes$: Observable<EmploymentTypeDto[]> = this.dictionaryService.getEmploymentTypes(true);
  workingTimes$: Observable<DictionaryItem[]> = this.dictionaryService.getWorkingTimes();

  selectedEmploymentType = new BehaviorSubject<EmploymentTypeDto>(null);
  isError = new BehaviorSubject<boolean>(false);
  formTouched = new BehaviorSubject<boolean>(false);

  private files: FileDto[] = [];

  private readonly timeBetweenInput = 300;
  private readonly minimumInputLetters = 2;
  private readonly unsubscribe$ = new Subject<void>();

  protected employmentHistoryRecord: EmploymentHistoryRecordDto;

  isEmploymentContract$: Observable<boolean>;
  agreementTerminationOptions$: Observable<DictionaryItem[]>;

  alertType$ = combineLatest([this.isError, this.formTouched]).pipe(
    map(([isError, touched]) => (isError && touched ? AlertType.Error : AlertType.Warning)),
  );


  constructor() {
    this.buildEmploymentHistoryRecordFormGroup();
    this.initializeObservables();
    this.onEmployerChange();
    this.onEmploymentTypeChange();
    if (this.data.employmentHistoryRecordId) {
      this.fetchEmploymentHistoryRecordById(this.data.employmentHistoryRecordId);
    }
  }

  get isReadonly(): boolean {
    return this.employmentHistoryRecord?.WorkerAgreementId !== null && this.employmentHistoryRecord?.WorkerAgreementId !== undefined;
  }

  get employerControl(): UntypedFormControl {
    return this.employmentHistoryRecordForm.get('employer') as UntypedFormControl;
  }

  get employerId(): number {
    const value = this.employmentHistoryRecordForm.get('employer').value;
    return typeof value === 'string' || value.Id === 0 ? null : value.Id;
  }

  get employerName(): string {
    const value = this.employmentHistoryRecordForm.get('employer').value;
    return typeof value === 'string' ? value : value.Name;
  }

  get employmentDateFrom(): Date {
    return this.employmentHistoryRecordForm.get('employmentDateFrom').value;
  }

  get employmentDateTo(): Date {
    return this.employmentHistoryRecordForm.get('employmentDateTo').value;
  }

  get employmentType(): UntypedFormControl {
    return this.employmentHistoryRecordForm?.get('employmentType') as UntypedFormControl;
  }

  get workingTimeControl() {
    return this.employmentHistoryRecordForm.get('workingTime');
  }

  get terminationOptionControl(): UntypedFormControl {
    return this.employmentHistoryRecordForm.get('terminationOption') as UntypedFormControl;
  }

  get formValues(): any {
    return this.employmentHistoryRecordForm.value;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  onFilesChange(files: File[]): void {
    this.spinner.show();
    this.files = [];
    let counter = 0;

    files.forEach((file) => {
      getBase64(file, (base64) => {
        this.files.push({ OriginalName: file.name, FileContent: base64 });
        counter++;
      });
    });

    const interval = setInterval(() => {
      if (counter == files.length) {
        this.spinner.hide();
        this.isError.next(this.shouldErrorBeDisplayed(this.selectedEmploymentType.value, this.files, this.employmentHistoryRecord?.Files));
        clearInterval(interval);
      }
    }, 200);
  }

  submit() {
    if (this.employmentHistoryRecordForm.invalid || this.isError.value || this.isReadonly) {
      this.employmentHistoryRecordForm.markAllAsTouched();
      this.formTouched.next(true);
      return;
    }

    this.spinner.show();
    const payload = this.createRequest();

    if (this.data.employmentHistoryRecordId) {
      this.updateEmploymentHistoryRecord(this.data.workerId, this.data.employmentHistoryRecordId, payload);
    } else {
      this.createEmploymentHistoryRecord(this.data.workerId, payload);
    }
  }

  close() {
    this.dialogRef.close();
  }

  displayValue(value: DictionaryItem): string | undefined {
    return value?.Name;
  }

  private buildEmploymentHistoryRecordFormGroup() {
    this.employmentHistoryRecordForm = this.formBuilder.group({
      address: [null, [Validators.required, Validators.maxLength(250)]],
      employer: [null, Validators.required],
      employmentDateFrom: [null, Validators.required],
      employmentDateTo: [null],
      employmentType: [null, Validators.required],
      workingTime: [null],
      terminationOption: [null, Validators.required],
    });
  }

  private patchEmploymentHistoryRecordFormGroupValue(record: any) {
    if (!record) {
      return;
    }

    const isEmploymentContract = [EmploymentType.EmploymentAgreement, EmploymentType.TemporaryEmploymentAgreement].includes(record.EmploymentTypeId);
    const terminationValue = this.isReadonly
      ? isEmploymentContract
        ? record.AgreementTerminationMethod
        : record.AgreementTerminationMode
      : isEmploymentContract
        ? record.AgreementTerminationMethodId
        : record.AgreementTerminationModeId;

    this.employmentHistoryRecordForm.patchValue({
      address: record.Address,
      employer: { Id: record.EmployerId, Name: record.Employer },
      employmentDateFrom: record.EmploymentDateFrom,
      employmentDateTo: record.EmploymentDateTo,
      employmentType: record.EmploymentTypeId,
      workingTime: record.WorkingTimeId,
      terminationOption: terminationValue,
    });
  }

  private createRequest(): CreateOrUpdateWorkerEmploymentHistoryRecordRequest {
    const isEmploymentContract = [EmploymentType.EmploymentAgreement, EmploymentType.TemporaryEmploymentAgreement].includes(
      this.formValues.employmentType,
    );

    return {
      Address: this.formValues.address,
      EmployerId: this.employerId,
      EmployerName: this.employerName,
      EmploymentDateFrom: this.formValues.employmentDateFrom,
      EmploymentDateTo: this.formValues.employmentDateTo,
      WorkingTimeId: this.formValues.workingTime,
      EmploymentTypeId: this.formValues.employmentType,
      AgreementTerminationMethodId: isEmploymentContract ? this.formValues.terminationOption : null,
      AgreementTerminationModeId: isEmploymentContract ? null : this.formValues.terminationOption,
      Files: this.files,
    };
  }

  private createEmploymentHistoryRecord(workerId: number, payload: CreateOrUpdateWorkerEmploymentHistoryRecordRequest) {
    this.workerService
      .createWorkerEmploymentHistoryRecord(workerId, payload)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => {
        this.snackbarService.openSuccessSnackBar(Messages.SuccessfullyCreatedEmploymentHistoryRecord);
        this.dialogRef.close(true);
      });
  }

  private updateEmploymentHistoryRecord(
    workerId: number,
    employmentHistoryRecordId: number,
    payload: CreateOrUpdateWorkerEmploymentHistoryRecordRequest,
  ) {
    this.workerService
      .updateWorkerEmploymentHistoryRecord(workerId, employmentHistoryRecordId, payload)
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => {
        this.snackbarService.openSuccessSnackBar(Messages.SuccessfullyUpdatedEmploymentHistoryRecord);
        this.dialogRef.close(true);
      });
  }

  private initializeObservables() {
    this.isEmploymentContract$ = this.employmentType.valueChanges.pipe(
      startWith(null),
      map((employmentTypeId) => [EmploymentType.EmploymentAgreement, EmploymentType.TemporaryEmploymentAgreement].includes(employmentTypeId)),
      shareReplay(1),
    );

    this.agreementTerminationOptions$ = combineLatest([this.isEmploymentContract$, this.employmentType.valueChanges.pipe(startWith(null))]).pipe(
      switchMap(([isEmploymentContract, employmentTypeId]) => {
        if (!employmentTypeId) return of([]);

        return isEmploymentContract
          ? this.agreementTerminationMethods$
          : this.dictionaryService.getAgreementTerminationModes(undefined, employmentTypeId);
      }),
      shareReplay(1),
    );
  }

  private onEmploymentTypeChange() {
    combineLatest([this.employmentTypes$, this.isEmploymentContract$])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([employmentTypes, isEmploymentContract]) => {
        const employmentTypeId = this.employmentType.value;

        const employmentType = employmentTypes.find((et) => et.Id === employmentTypeId);
        this.selectedEmploymentType.next(employmentType);

        if (!this.isReadonly) {
          this.terminationOptionControl.setValue(null);
        }

        if (!isEmploymentContract) {
          this.workingTimeControl.setValue(null);
        }

        this.updateWorkingTimeValidation(isEmploymentContract);

        if (employmentType) {
          const shouldErrorBeDisplayed = this.shouldErrorBeDisplayed(employmentType, this.files, this.employmentHistoryRecord?.Files);
          this.isError.next(shouldErrorBeDisplayed);
          this.formTouched.next(false);
        }
      });
  }

  private updateWorkingTimeValidation(isRequired: boolean) {
    this.workingTimeControl.setValidators(isRequired ? [Validators.required] : null);
    this.workingTimeControl.updateValueAndValidity();
  }

  private onEmployerChange() {
    this.employers$ = this.employerControl.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      switchMap((value: string) => (value && value.length > this.minimumInputLetters ? this.dictionaryService.getEmployers(value) : of([]))),
    );
  }

  private shouldErrorBeDisplayed = (employmentType: EmploymentTypeDto, addedFiles: FileDto[], savedfiles: string[]) =>
    employmentType.IsFileRequired && !addedFiles.length && !savedfiles?.length;

  private fetchEmploymentHistoryRecordById(employmentHistoryRecordId: number) {
    this.employmentHistoryRecordService
      .getEmploymentHistoryRecordById(employmentHistoryRecordId)
      .pipe(first())
      .subscribe(async (res: EmploymentHistoryRecordDto) => {
        this.employmentHistoryRecord = res;
        this.patchEmploymentHistoryRecordFormGroupValue(res);

        if (this.isReadonly) {
          this.employmentHistoryRecordForm.disable();
        }
      });
  }
}
